// Paused 3D snapshot of the layer stack. When active, captures every
// layer's material grid (main world + each dimension), bakes it to a
// texture with the same palette the 2D renderer uses, and shows the
// layers as planes stacked in depth. Drag to orbit, wheel to zoom.
// Pure visualisation — it never ticks the sim.

import { useEffect, useRef } from "react";
import * as THREE from "three";

import type { MaterialDef, SandboxLike } from "../sim/types";
import { buildStyleTable, type StyleTable } from "../three/styles";

interface ThreeDViewProps {
  active: boolean;
  sandbox: SandboxLike | null;
  materials: MaterialDef[];
}

function bakeTexture(
  mats: Uint16Array,
  seeds: Uint8Array,
  w: number,
  h: number,
  styles: StyleTable,
): THREE.DataTexture {
  const rgba = new Uint8Array(w * h * 4);
  for (let i = 0; i < mats.length; i++) {
    const m = mats[i] >>> 0;
    const shader = styles[m] ?? styles[0];
    shader(rgba, i * 4, seeds[i] ?? 0, 0);
  }
  const tex = new THREE.DataTexture(rgba, w, h, THREE.RGBAFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.flipY = true; // grid row 0 is the top of the layer
  tex.needsUpdate = true;
  return tex;
}

export function ThreeDView(props: ThreeDViewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const sandbox = props.sandbox;
    if (!props.active || !canvas || !sandbox || !sandbox.layerCount) return;
    // The per-layer accessors only exist on the wasm sandbox.
    if (!sandbox.snapshotLayer || !sandbox.layerWidth || !sandbox.layerHeight) {
      return;
    }

    const styles = buildStyleTable(props.materials);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    const scene = new THREE.Scene();

    // Build a plane per layer, sized to its real cell count (so a 128²
    // dimension is visibly half the 256² main world), stacked in depth.
    const count = sandbox.layerCount();
    const sizes: { w: number; h: number }[] = [];
    let maxDim = 1;
    for (let i = 0; i < count; i++) {
      const w = sandbox.layerWidth(i);
      const h = sandbox.layerHeight(i);
      sizes.push({ w, h });
      maxDim = Math.max(maxDim, w, h);
    }
    const SPACING = 0.6;
    const disposables: { dispose(): void }[] = [];
    for (let i = 0; i < count; i++) {
      const { w, h } = sizes[i];
      if (w === 0 || h === 0) continue;
      const mats = sandbox.snapshotLayer(i);
      const seeds = sandbox.snapshotLayerSeeds?.(i) ?? new Uint8Array(w * h);
      const tex = bakeTexture(mats, seeds, w, h, styles);
      const geo = new THREE.PlaneGeometry((2 * w) / maxDim, (2 * h) / maxDim);
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.z = -i * SPACING;
      scene.add(mesh);
      disposables.push(tex, geo, mat);
    }

    // Hand-rolled orbit camera (three's OrbitControls examples module
    // isn't bundled in this install).
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    const target = new THREE.Vector3(0, 0, -((count - 1) * SPACING) / 2);
    let theta = 0.5;
    let phi = 1.1;
    let radius = Math.max(4, count * SPACING + 3);
    const place = () => {
      camera.position.set(
        target.x + radius * Math.sin(phi) * Math.sin(theta),
        target.y + radius * Math.cos(phi),
        target.z + radius * Math.sin(phi) * Math.cos(theta),
      );
      camera.lookAt(target);
    };

    const resize = () => {
      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    place();

    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (e: PointerEvent) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      theta -= (e.clientX - lastX) * 0.01;
      phi = Math.min(Math.PI - 0.05, Math.max(0.05, phi - (e.clientY - lastY) * 0.01));
      lastX = e.clientX;
      lastY = e.clientY;
      place();
    };
    const onUp = (e: PointerEvent) => {
      dragging = false;
      canvas.releasePointerCapture(e.pointerId);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      radius = Math.min(60, Math.max(1.5, radius * (1 + e.deltaY * 0.001)));
      place();
    };
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", resize);

    let rafId = 0;
    const loop = () => {
      resize();
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", resize);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
    };
  }, [props.active, props.sandbox, props.materials]);

  if (!props.active) return null;
  return <canvas ref={canvasRef} className="threed-view" />;
}
